<template>
  <div class="p-6">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-2xl font-semibold">My Bookshelf</h2>
      <Button variant="outline" size="sm" :disabled="isSharingSet" @click="handleShareSet">
        <Icon v-if="!isSharingSet" name="heroicons:share" class="h-4 w-4 mr-1.5" />
        <Icon v-else name="heroicons:arrow-path" class="h-4 w-4 mr-1.5 animate-spin" />
        {{ isSharingSet ? 'Creating Link...' : 'Share Book Set' }}
      </Button>
    </div>

    <!-- Search Input -->
    <div class="mb-6">
      <FilterInput v-model="searchQuery" placeholder="Search books by title..." />
    </div>

    <!-- Random Book Suggestion -->
    <div class="mb-8">
      <h3 class="text-xl font-semibold mb-4">Random Book Suggestion</h3>
      <RandomBook :book="randomBook" @refresh="getRandomBook" @update:bubble="handleBubbleUpdate" />
    </div>

    <!-- Filters Section - Wrapped in ClientOnly -->
    <ClientOnly>
      <Collapsible v-model:open="isFiltersOpen" class="mb-8 border rounded-lg p-4">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-semibold">Filter Books</h3>
          <CollapsibleTrigger as-child>
            <Button variant="ghost" size="sm" class="w-9 p-0">
              <!-- Using Nuxt Icon component - Assuming Icon is globally available or imported -->
              <Icon
                :name="isFiltersOpen ? 'lucide:chevrons-up-down' : 'lucide:chevrons-down-up'"
                class="h-4 w-4"
              />
              <span class="sr-only">Toggle Filters</span>
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <!-- Author Filter -->
            <div>
              <h4 class="text-sm font-medium mb-2">Author</h4>
              <TypeSelector
                v-model="selectedAuthor"
                :types="bookStore.uniqueAuthors"
                placeholder="Select Author"
              />
            </div>

            <!-- Category Filter -->
            <div>
              <h4 class="text-sm font-medium mb-2">Category</h4>
              <TypeSelector
                v-model="selectedCategory"
                :types="bookStore.uniqueCategories"
                placeholder="Select Category"
              />
            </div>

            <!-- Publisher Filter -->
            <div>
              <h4 class="text-sm font-medium mb-2">Publisher</h4>
              <TypeSelector
                v-model="selectedPublisher"
                :types="bookStore.uniquePublishers"
                placeholder="Select Publisher"
              />
            </div>

            <!-- Read Status Filter -->
            <div>
              <h4 class="text-sm font-medium mb-2">Read Status</h4>
              <Select v-model="selectedReadStatus">
                <SelectTrigger>
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Status</SelectItem>
                  <SelectItem value="Read">Read</SelectItem>
                  <SelectItem value="Unread">Unread</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <!-- Publication Year Filter -->
            <div class="md:col-span-2 lg:col-span-1">
              <h4 class="text-sm font-medium mb-2">Publication Year</h4>
              <div class="flex items-center gap-2">
                <Input
                  v-model="minYearInput"
                  type="number"
                  placeholder="Min Year"
                  class="flex-1"
                  :min="bookStore.publicationYearRange.min ?? undefined"
                  :max="bookStore.publicationYearRange.max ?? undefined"
                  @blur="applyYearFilters"
                />
                <span>-</span>
                <Input
                  v-model="maxYearInput"
                  type="number"
                  placeholder="Max Year"
                  class="flex-1"
                  :min="bookStore.publicationYearRange.min ?? undefined"
                  :max="bookStore.publicationYearRange.max ?? undefined"
                  @blur="applyYearFilters"
                />
              </div>
              <p
                v-if="bookStore.publicationYearRange.min !== null"
                class="text-xs text-muted-foreground mt-1"
              >
                Available range: {{ bookStore.publicationYearRange.min }} -
                {{ bookStore.publicationYearRange.max }}
              </p>
            </div>
          </div>
          <div class="flex justify-end border-t pt-4 mt-4">
            <Button variant="ghost" @click="bookStore.resetFilters()">Reset All Filters</Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
      <!-- Fallback while loading ClientOnly content -->
      <template #fallback>
        <div class="mb-8 border rounded-lg p-4">
          <div class="flex justify-between items-center">
            <h3 class="text-xl font-semibold">Filter Books</h3>
            <!-- Placeholder for button -->
            <div class="h-9 w-9 bg-muted rounded animate-pulse"></div>
          </div>
        </div>
      </template>
    </ClientOnly>

    <!-- Loading / Error / Empty States / Books Grid - Wrapped in ClientOnly -->
    <ClientOnly>
      <div v-if="isLoading && !paginatedBooks.length" class="text-center py-8">
        <p class="text-muted-foreground">Loading books...</p>
        <!-- Consider adding a spinner component here -->
      </div>
      <div v-else-if="error" class="text-center py-8 text-destructive">
        <p>{{ error }}</p>
      </div>
      <div v-else-if="!bookStore.books.length && !isLoading" class="text-center py-8">
        <p class="text-muted-foreground mb-4">You haven't added any books yet.</p>
        <Button @click="navigateToAddBook">Add Your First Book</Button>
      </div>
      <div v-else-if="!paginatedBooks.length && !isLoading" class="text-center py-8">
        <p class="text-muted-foreground mb-4">No books match your current filters.</p>
        <Button variant="ghost" @click="bookStore.resetFilters()">Clear Filters</Button>
      </div>
      <!-- Books Grid -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AppLink v-for="book in paginatedBooks" :key="book.uuid" :to="`/bubble/${book.uuid}`">
          <!-- Book Card Content -->
          <div
            class="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer bg-card h-full flex flex-col min-h-[180px]"
          >
            <!-- Top section: Image and Core Details -->
            <div class="flex items-start mb-3 flex-grow">
              <!-- Image Column -->
              <div
                v-if="
                  book.meta?.offlineMediaResources?.cover_photo ||
                  book.smallThumbnail ||
                  book.coverPhoto
                "
                class="w-1/4 pr-4 flex-shrink-0"
              >
                <img
                  :src="
                    book.meta?.offlineMediaResources?.cover_photo ||
                    book.smallThumbnail ||
                    book.coverPhoto ||
                    undefined
                  "
                  :alt="book.title"
                  class="w-full h-auto object-cover rounded aspect-[2/3]"
                  loading="lazy"
                />
              </div>
              <!-- Details Column -->
              <div class="flex-grow min-w-0">
                <h3 class="text-base font-medium mb-1 truncate" :title="book.title">
                  {{ book.title }}
                </h3>
                <p
                  class="text-sm text-muted-foreground mb-2 line-clamp-2"
                  :title="book.authors?.join(', ') || 'Unknown Author'"
                >
                  {{ book.authors?.join(', ') || 'Unknown Author' }}
                </p>
                <!-- Additional Details -->
                <div class="text-xs text-muted-foreground space-y-0.5 mt-1">
                  <p v-if="book.publisher" class="truncate" :title="book.publisher">
                    Pub: {{ book.publisher }}
                  </p>
                  <!-- Call the getYear function -->
                  <p v-if="getYear(book.publicationDate)">
                    Year: {{ getYear(book.publicationDate) }}
                  </p>
                  <p v-if="book.pageCount">Pages: {{ book.pageCount }}</p>
                </div>
              </div>
            </div>
            <!-- Footer Area: Badges/Categories -->
            <div class="mt-auto pt-2 flex flex-wrap items-center gap-1 border-t border-border/50">
              <Badge v-if="book.read" variant="secondary" class="text-xs font-normal">Read</Badge>
              <Badge v-else variant="outline" class="text-xs font-normal">Unread</Badge>
              <!-- Limited Categories -->
              <span
                v-for="category in book.categories?.slice(0, 2)"
                :key="category"
                class="text-xs bg-muted text-foreground px-1.5 py-0.5 rounded-full truncate"
                :title="category"
              >
                {{ category }}
              </span>
              <span v-if="(book.categories?.length || 0) > 2" class="text-xs text-muted-foreground"
                >+{{ (book.categories?.length || 0) - 2 }} more</span
              >
            </div>
          </div>
          <!-- End Book Card Content -->
        </AppLink>
      </div>
      <!-- Fallback for the Book List Area -->
      <template #fallback>
        <div class="text-center py-8">
          <p class="text-muted-foreground">Loading books...</p>
          <!-- You could add a more sophisticated skeleton loader here -->
        </div>
      </template>
    </ClientOnly>

    <!-- Load More Button (outside ClientOnly for book list) -->
    <ClientOnly>
      <div v-if="canLoadMore" class="mt-6 text-center">
        <Button variant="default" class="hover:opacity-90" :disabled="isLoading" @click="loadMore">
          {{ isLoading ? 'Loading...' : 'Load More Books' }}
        </Button>
      </div>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, h } from 'vue'
import { useBubblesStore } from '~/stores/bubbles'
import { useBookStore } from '~/stores/core/bookStore'
import { useUserShareStore } from '~/stores/userShare'
import type { BookBubble } from '~/stores/interfaces'
import RandomBook from '~/components/bubbles/view/helpers/RandomBook.vue'
import FilterInput from '~/components/bubbles/view/helpers/FilterInput.vue'
import TypeSelector from '~/components/bubbles/view/helpers/TypeSelector.vue'
import { useRouter } from 'vue-router'
import { useNavigationStore } from '@/stores/navigationStore'
import AppLink from '@/components/ui/link/AppLink.vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { logError } from '@/composables/useLogger'
import { useToast } from '@/components/ui/toast'
import CopyButton from '@/components/ui/button/CopyButton.vue'
import { Icon, ClientOnly } from '#components'
import { useClipboard } from '@vueuse/core'
import { getYear } from '~/utils/dateUtils'

const router = useRouter()
const bubblesStore = useBubblesStore()
const bookStore = useBookStore()
const navigationStore = useNavigationStore()
const userShareStore = useUserShareStore()
const { toast } = useToast()
const { copy, copied, isSupported } = useClipboard()

const BOOKS_PER_PAGE = 12
const page = ref(1)
const isLoading = ref(false)
const error = ref<string | null>(null)
const isFiltersOpen = ref(false)
const isSharingSet = ref(false)

const searchQuery = computed({
  get: () => bookStore.searchQuery,
  set: (value) => bookStore.setSearchQuery(value),
})

const selectedAuthor = computed({
  get: () => bookStore.selectedAuthor,
  set: (value) => bookStore.setFilters({ author: value }),
})

const selectedCategory = computed({
  get: () => bookStore.selectedCategory,
  set: (value) => bookStore.setFilters({ category: value }),
})

const selectedPublisher = computed({
  get: () => bookStore.selectedPublisher,
  set: (value) => bookStore.setFilters({ publisher: value }),
})

const selectedReadStatus = computed({
  get: () => bookStore.selectedReadStatus,
  set: (value: 'All' | 'Read' | 'Unread') => bookStore.setFilters({ readStatus: value }),
})

const minYearInput = ref<string>(bookStore.minPublicationYear?.toString() ?? '')
const maxYearInput = ref<string>(bookStore.maxPublicationYear?.toString() ?? '')

function applyYearFilters() {
  const minYear = minYearInput.value ? parseInt(minYearInput.value, 10) : null
  const maxYear = maxYearInput.value ? parseInt(maxYearInput.value, 10) : null
  if ((minYear !== null && isNaN(minYear)) || (maxYear !== null && isNaN(maxYear))) {
    return
  }
  bookStore.setPublicationYearRange(minYear, maxYear)
}

watch(
  () => [bookStore.minPublicationYear, bookStore.maxPublicationYear],
  ([min, max]) => {
    minYearInput.value = min?.toString() ?? ''
    maxYearInput.value = max?.toString() ?? ''
  }
)

const randomBook = ref<BookBubble | null>(null)

const paginatedBooks = computed(() => {
  return bookStore.filteredBooks.slice(0, page.value * BOOKS_PER_PAGE)
})

onMounted(async () => {
  bookStore.setSearchQuery('')
  bookStore.setFilters({ author: 'All', category: 'All' })
  page.value = 1

  try {
    isLoading.value = true
    error.value = null

    if (!bubblesStore.bubbleTypes['book']) {
      await bubblesStore.fetchBubbleTypes()
    }

    await bubblesStore.fetchBubblesByType('book')
    bookStore.books = (bubblesStore.bubblesByType['book'] || []).map(
      (bubble) => bubble as BookBubble
    )
    getRandomBook()
  } catch (err) {
    error.value = 'Failed to load books. Please try again later.'
    logError('Failed to fetch books:', err)
  } finally {
    isLoading.value = false
  }
})

async function loadMore() {
  if (!canLoadMore.value || isLoading.value) return

  try {
    page.value++
    if (page.value * BOOKS_PER_PAGE >= bookStore.filteredBooks.length) {
      isLoading.value = true
      error.value = null
      await bubblesStore.fetchBubblesByType('book')
      bookStore.books = (bubblesStore.bubblesByType['book'] || []).map(
        (bubble) => bubble as BookBubble
      )
    }
  } catch (err) {
    error.value = 'Failed to load more books. Please try again later.'
    logError('Failed to fetch books:', err)
    page.value--
  } finally {
    isLoading.value = false
  }
}

const canLoadMore = computed(() => {
  return bookStore.filteredBooks.length > page.value * BOOKS_PER_PAGE
})

function getRandomBook() {
  randomBook.value = bookStore.getRandomBook()
}

function navigateToAddBook() {
  router.push('/dashboard/add')
}

const handleBubbleUpdate = (updatedBubble: BookBubble) => {
  bookStore.updateBook(updatedBubble)
  if (randomBook.value?.uuid === updatedBubble.uuid) {
    randomBook.value = updatedBubble
  }
}

watch(
  () => bookStore.filteredBooks,
  (books) => {
    navigationStore.setCurrentType('book')
    navigationStore.setCurrentTypeItems(books.map((book) => book.uuid))
  },
  { immediate: true }
)

watch(
  [
    searchQuery,
    selectedAuthor,
    selectedCategory,
    selectedPublisher,
    selectedReadStatus,
    () => bookStore.minPublicationYear,
    () => bookStore.maxPublicationYear,
  ],
  () => {
    page.value = 1
  },
  { deep: true }
)

const handleShareSet = async () => {
  isSharingSet.value = true
  try {
    const shareData = {
      shareType: 'set' as const,
      targetBubbleType: 'book',
      isPublic: true,
    }
    const result = await userShareStore.createShareLink(shareData)

    if (result && result.url) {
      toast({
        title: 'Share Link Created',
        description: 'Your set of books can be shared via this link.',
        duration: 9000,
        action: h(CopyButton, { textToCopy: result.url, variant: 'secondary' }),
      })
      if (isSupported.value) {
        await copy(result.url)
        if (copied.value) {
          toast({
            title: 'Link Copied!',
            description: 'The share link has been copied to your clipboard.',
            duration: 3000,
          })
        } else {
          toast({
            title: 'Copy Failed',
            description: 'Could not automatically copy the link. Please use the button.',
            variant: 'destructive',
            duration: 5000,
          })
        }
      }
    } else {
      if (!result) {
        toast({
          title: 'Error',
          description: 'Could not create share link for set.',
          variant: 'destructive',
        })
      }
    }
  } catch (error) {
    logError('Error creating book set share link:', error)
    toast({
      title: 'Error',
      description: 'An unexpected error occurred while sharing the set.',
      variant: 'destructive',
    })
  } finally {
    isSharingSet.value = false
  }
}
</script>
